/**
 * Axios API client for the aiSafePlate backend.
 *
 * Responsibilities:
 *  - Attach the JWT access token to every request.
 *  - On a 401, transparently exchange the refresh token for a new pair
 *    (once) and retry the original request.
 *  - If refresh fails, clear stored credentials and broadcast LOGOUT_EVENT
 *    so the AuthProvider can drop React state.
 *  - Normalize backend errors ({ error, message }) into Error(message).
 *
 * The exported get/post/put/del are module-level singletons, so callers
 * (e.g. useAPI) hand stable function references to React effect deps and
 * don't trigger refetch loops.
 */
import axios from 'axios';
import {
  API_BASE_URL,
  TOKEN_KEY,
  REFRESH_KEY,
  LOGOUT_EVENT,
} from '../config';

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Bare axios instance for the refresh call — no interceptors, so a failing
// refresh can't recurse back through the 401 handler.
const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

function clearAuthAndNotify() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  window.dispatchEvent(new Event(LOGOUT_EVENT));
}

// --- Request: attach bearer token -----------------------------------------
client.interceptors.request.use((cfg) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

// --- Response: one-shot refresh on 401 -------------------------------------
client.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Only attempt refresh for an authenticated request that 401'd once,
    // and never for the auth endpoints themselves.
    const isAuthCall = original?.url?.includes('/api/auth/');
    if (status === 401 && !original?._retried && !isAuthCall) {
      const refreshToken = localStorage.getItem(REFRESH_KEY);
      if (refreshToken) {
        try {
          original._retried = true;
          const { data } = await refreshClient.post('/api/auth/refresh', {
            refreshToken,
          });
          localStorage.setItem(TOKEN_KEY, data.accessToken);
          if (data.refreshToken) {
            localStorage.setItem(REFRESH_KEY, data.refreshToken);
          }
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${data.accessToken}`;
          return client(original);
        } catch (refreshErr) {
          clearAuthAndNotify();
          return Promise.reject(refreshErr);
        }
      }
      // No refresh token to fall back on — force logout.
      clearAuthAndNotify();
    }
    return Promise.reject(error);
  }
);

/**
 * Turn an axios error into a plain Error carrying the backend's message,
 * so components can surface `err.message` directly.
 */
function toError(error) {
  const body = error.response?.data;
  const msg =
    body?.message ||
    body?.error ||
    error.message ||
    'Request failed. Please try again.';
  const e = new Error(msg);
  e.status = error.response?.status;
  return e;
}

export async function get(url, config) {
  try {
    const res = await client.get(url, config);
    return res.data;
  } catch (error) {
    throw toError(error);
  }
}

export async function post(url, data, config) {
  try {
    const res = await client.post(url, data, config);
    return res.data;
  } catch (error) {
    throw toError(error);
  }
}

export async function put(url, data, config) {
  try {
    const res = await client.put(url, data, config);
    return res.data;
  } catch (error) {
    throw toError(error);
  }
}

export async function del(url, config) {
  try {
    const res = await client.delete(url, config);
    return res.data;
  } catch (error) {
    throw toError(error);
  }
}

export default client;
